import React, { useEffect, ForwardRefExoticComponent, RefAttributes, useRef } from "react";
import { createStyles, makeStyles, TextFieldProps, Theme } from "@material-ui/core";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { useHistory, useParams } from "react-router-dom";
import Grid from "@material-ui/core/Grid/Grid";
import CustomButton from "../../../components/Button/CustomButton";
import ProductApi, { CompetitorCompany } from "../../../api/services/ProductApi";
import useEnv from "../../../hooks/env";
import RequestsPagePathParams from "../../../api/models/test-request/RequestsPagePathParams";
import { CompetitorSampleAddFormType } from "../../../api/models/product/CompetitorSampleAddFormType";
import CustomSearchAutoComplete, {
  CustomSearchAutocompleteProps,
} from "../../../components/Autocomplete/CustomSearchAutoComplete";
import { TestRequestTabType } from "../useTestRequestParams";
import CompetitorProduct from "../../../api/models/product/CompetitorProduct";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    alignRight: {
      textAlign: "right",
    },
    marginTop: {
      marginTop: theme.spacing(2),
    },
  }),
);

const CompetitorCompanyAutocomplete = CustomSearchAutoComplete as ForwardRefExoticComponent<
  MergedProps<CustomSearchAutocompleteProps<CompetitorCompany>, TextFieldProps> & RefAttributes<{ reset: () => void }>
>;

const CompetitorProductAutocomplete = CustomSearchAutoComplete as ForwardRefExoticComponent<
  CustomSearchAutocompleteProps<CompetitorProduct> & RefAttributes<{ reset: () => void }>
>;

interface CompetitorSampleAddFormProps {
  hideSubmit?: boolean;
  handleFormChange?: (values: CompetitorSampleAddFormType) => void;
  handleSubmit?: (values: CompetitorSampleAddFormType) => void;
}

const CompetitorSampleAddForm = (props: CompetitorSampleAddFormProps) => {
  const { api } = useEnv();
  const competitorCompanySearch = useRef<{ reset: () => void }>(null);
  const productApi = new ProductApi(api);
  const history = useHistory();
  const { testRequestId, tabName } = useParams<RequestsPagePathParams>();
  const classes = useStyles(props);
  const { hideSubmit, handleFormChange, handleSubmit } = props;

  const errorHelperText = "Please choose";
  const yupValidationSchema = Yup.object().shape({
    company: Yup.mixed().required(errorHelperText),
    product: Yup.string().required(errorHelperText),
  });

  const handleNavigation = () => {
    localStorage.setItem("recentReqId", testRequestId);
    localStorage.setItem("recentTab", tabName);
    localStorage.setItem(
      "recentDialog",
      tabName === TestRequestTabType.PRODUCT.toString()
        ? "addProductCompetitor"
        : "addSubstrateSample?type=COMPETITOR_PRODUCT",
    );

    history.push("/products/competitor/createProduct");
  };

  return (
    <Formik<CompetitorSampleAddFormType>
      initialValues={{
        company: null,
        product: null,
      }}
      onSubmit={(values) => {
        if (handleSubmit) {
          handleSubmit(values);
        }
      }}
      validationSchema={yupValidationSchema}
    >
      {(formikProps: FormikProps<CompetitorSampleAddFormType>): JSX.Element => {
        const { errors, isSubmitting, setFieldValue, touched, values } = formikProps;

        useEffect(() => {
          if (handleFormChange) {
            handleFormChange(values);
          }
        }, [values]);

        return (
          <Form>
            <Grid container justify="flex-start" direction="row" spacing={4}>
              <Grid item xs={6}>
                <CompetitorCompanyAutocomplete
                  autoFocus
                  error={!!(errors.company && touched.company)}
                  inputLabel="Company"
                  map={(value): { key: string; label: string } => ({
                    key: `${value.id}`,
                    label: value.name as string,
                  })}
                  optionFetch={(term: string) => {
                    return productApi.getCompetitorCompanies(term).then((res) => res?.data);
                  }}
                  onChange={(e, val) => {
                    setFieldValue("company", val);
                    setFieldValue("product", null);
                    competitorCompanySearch.current?.reset();
                  }}
                  value={values.company}
                />
              </Grid>
              <Grid item xs={6}>
                <CompetitorProductAutocomplete
                  ref={competitorCompanySearch}
                  disabled={!values.company}
                  inputLabel="Product"
                  map={(value): { key: string; label: string } => ({
                    key: `${value.id}`,
                    label: value.name as string,
                  })}
                  optionFetch={(term: string) => {
                    return productApi.getCompetitorProduct(term, values.company!.id).then((res) => res?.data);
                  }}
                  onChange={(e, val) => formikProps.setFieldValue("product", val)}
                  value={values.product}
                  error={!!(errors.product && touched.product)}
                />
              </Grid>
            </Grid>
            <Grid container item direction="row" justify="flex-start">
              <span onClick={handleNavigation} className="helperTextLink" role="link" tabIndex={0}>
                Not found? Create new Competitor Product
              </span>
            </Grid>
            {!hideSubmit && (
              <Grid container item direction="row" justify="flex-end">
                <CustomButton className={classes.marginTop} disabled={isSubmitting} primary text="Use" type="submit" />
              </Grid>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

export default CompetitorSampleAddForm;
